import { useRef, useState, useEffect } from "react";
import Modal from "react-modal";
import { useReactToPrint } from "react-to-print";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import { useTranslation } from "react-i18next";
// New icons needed for Time and Days
import AccessTimeIcon from "@mui/icons-material/AccessTime"; 
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"; 
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import SportsGymnasticsIcon from "@mui/icons-material/SportsGymnastics";
import TuneIcon from "@mui/icons-material/Tune";
import {
  BmiCategory,
  GoalOverview,
  NutritionStratergy,
  WorkoutFocus,
  LongTerm,
} from "@utils/defaultImages";

const AdviseModal = ({
  showModal,
  setShowModal,
  targetWeight,
  timeline,
  durationType,
  message,
  handleApplyGoal,
  targetWeightUnit,
  maintenanceCalories,
  targetDailyCalories,
  dailyCalorieAdjustment,
  goalBodyFat,
  goalWaistCircumference,
  dynamicOptions,

  // 🚀 ASSUMED NEW PROPS FOR RAW IDs/VALUES
  rawWorkoutsCategories,
  rawEquipmentCategories,
  rawDifficultiesLevel,

  suggestedWorkouts,
  suggestedEquipment,
  suggestedDifficulty,
  setSuggestedWorkouts,
  setSuggestedEquipment,
  setSuggestedDifficulty,

  // ⭐ NEW PROPS ADDED
  dailyExerciseTimePreference,
  selectableDays,
}) => {
  const { t } = useTranslation();

  const getDisplayName = (key, categoryId = null) => {
    if (!categoryId || !dynamicOptions?.[key]) return categoryId || "";

    const item = dynamicOptions[key].find((item) => item?.id === categoryId);
    return item ? item.name : categoryId;
  };

  // Map IDs to names when component receives props
  useEffect(() => {
    if (dynamicOptions) {
      // 🚀 CHANGE: Use rawWorkoutsCategories instead of message.workoutsCategories
      if (rawWorkoutsCategories && dynamicOptions.workoutsCategories) {
        const workoutNames = rawWorkoutsCategories.map((workoutId) => {
          const workout = dynamicOptions.workoutsCategories.find(
            (cat) => cat.id === workoutId
          );
          return workout ? workout.name : workoutId; // Fallback to ID if not found
        });
        setSuggestedWorkouts(workoutNames);
      }

      // 🚀 CHANGE: Use rawEquipmentCategories instead of message.equipmentCategories
      if (rawEquipmentCategories && dynamicOptions.equipmentCategories) {
        const equipment = dynamicOptions.equipmentCategories.find(
          (eq) => eq.id === rawEquipmentCategories
        );
        setSuggestedEquipment(
          equipment ? equipment.name : rawEquipmentCategories
        );
      }

      // 🚀 CHANGE: Use rawDifficultiesLevel instead of message.difficultiesLevel
      if (rawDifficultiesLevel) {
        setSuggestedDifficulty(rawDifficultiesLevel);
      }
    }
  }, [
    rawWorkoutsCategories,
    rawEquipmentCategories,
    rawDifficultiesLevel,
    dynamicOptions,
    setSuggestedWorkouts,
    setSuggestedEquipment,
    setSuggestedDifficulty,
  ]); // Updated dependencies

  const nutrientData = [
    {
      label: t("BMI & Category"),
      key: "average_sleep_duration",
      icon: (
        <BmiCategory className="w-[20px] h-[20px] md:w-[24px] md:h-[24px]" />
      ),
      tabColor: "#F0FFFF",
      value: message?.["BMI & Category"],
    },
    {
      label: t("Goal Overview"),
      key: "wake_ups_per_week",
      tabColor: "#F0FFFF",
      icon: (
        <GoalOverview className="w-[20px] h-[20px] md:w-[24px] md:h-[24px]" />
      ),
      value: message?.["Goal Overview"],
    },
    {
      label: t("Nutrition Strategy"),
      key: "sleep_consistency_days",
      tabColor: "#F0FFFF",
      icon: (
        <NutritionStratergy className="w-[20px] h-[20px] md:w-[24px] md:h-[24px]" />
      ),
      value: message?.["Nutrition Strategy"],
    },
    {
      label: t("Workout Focus"),
      key: "advice_followed",
      tabColor: "#F0FFFF",
      icon: (
        <WorkoutFocus className="w-[20px] h-[20px] md:w-[24px] md:h-[24px]" />
      ),
      value: message?.["Workout Focus"],
    },
    {
      label: t("Long-Term Plan"),
      key: "advice_followed",
      tabColor: "#F0FFFF",
      icon: <LongTerm className="w-[20px] h-[20px] md:w-[24px] md:h-[24px]" />,
      value: message?.["Long-Term Plan"],
    },
  ];

  return (
    <div>
      <Modal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        className="bg-white rounded-lg w-[95%] md:w-[90%] lg:w-[85%] xl:w-[90%] relative overflow-y-auto outline-none p-4 md:p-[20px] max-h-[90vh] md:max-h-[90vh]"
        overlayClassName="fixed inset-0 flex items-center justify-center backdrop-blur-xl p-4"
      >
        <div className="flex items-center justify-end mb-3 md:mb-2">
          <button onClick={() => setShowModal(false)}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="24" height="24" rx="12" fill="#0575E6" />
              <path
                d="M16 8L8 16"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 8L16 16"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <h2 className="text-[18px] md:text-[24px] lg:text-[30px] text-center font-[600] text-black-600">
          {t("firstFormSteps.automaticGoalHeading")}
        </h2>

        <h1 className="text-blue-600 text-center my-3 text-[14px] md:text-[16px]">
          {t("firstFormSteps.automaticGoalModalHeading")}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 md:gap-3">
          {/* Input fields section (lg:col-span-3) */}
          <div className="flex flex-col gap-3 md:gap-7 lg:col-span-3">
            {/* First to Fourth rows of input fields remain here... */}
            <div className="flex flex-col md:flex-row gap-3 md:gap-2">
              <div className="w-full md:w-1/2">
                <span className="text-[#414651] text-[12px] sm:text-[14px] md:text-[16px] truncate max-w-[150px] block">
                  {t(`firstFormSteps.targetWeight.label`)}
                </span>
                <div className="relative">
                  <input
                    type="text"
                    className="border p-2 pl-3 pr-10 rounded-md w-full bg-gray-100 cursor-not-allowed h-10 text-[14px]"
                    value={targetWeight}
                    readOnly
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-[12px] md:text-sm">
                    {targetWeightUnit}
                  </span>
                </div>
              </div>

              <div className="w-full md:w-1/2">
                <span className="text-[#414651] text-[12px] sm:text-[14px] md:text-[16px] truncate max-w-[150px] block">
                  {t(`firstFormSteps.timeline.label`)}
                </span>
                <input
                  type="text"
                  className="border p-2 pl-3 rounded-md w-full bg-gray-100 cursor-not-allowed h-10 text-[14px]"
                  value={timeline}
                  readOnly
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3 md:gap-2">
              <div className="w-full md:w-1/2">
                <span className="text-[#414651] text-[12px] sm:text-[14px] md:text-[16px] truncate max-w-[150px] block">
                  {t("Goal Body Fat")}
                </span>
                <div className="relative">
                  <input
                    type="text"
                    className="border p-2 pl-3 pr-10 rounded-md w-full bg-gray-100 cursor-not-allowed h-10 text-[14px]"
                    value={goalBodyFat}
                    readOnly
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-[12px] md:text-sm">
                    %
                  </span>
                </div>
              </div>

              <div className="w-full md:w-1/2">
                <span className="text-[#414651] text-[12px] sm:text-[14px] md:text-[16px] truncate max-w-[150px] block">
                  {t("Goal Waist Circumference")}
                </span>
                <div className="relative">
                  <input
                    type="text"
                    className="border p-2 pl-3 pr-10 rounded-md w-full bg-gray-100 cursor-not-allowed h-10 text-[14px]"
                    value={goalWaistCircumference}
                    readOnly
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-[12px] md:text-sm">
                    cm
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3 md:gap-2">
              <div className="w-full md:w-1/2">
                <span className="text-[#414651] text-[12px] sm:text-[14px] md:text-[16px] truncate max-w-[150px] block">
                  {t(`firstFormSteps.durationType.label`)}
                </span>
                <input
                  type="text"
                  className="border p-2 pl-3 rounded-md w-full bg-gray-100 cursor-not-allowed h-10 text-[14px]"
                  value={
                    t(`firstFormSteps.durationType.options.${durationType}`) ||
                    durationType
                  }
                  readOnly
                />
              </div>

              <div className="w-full md:w-1/2">
                <span className="text-[#414651] text-[12px] sm:text-[14px] md:text-[16px] truncate max-w-[150px] block">
                  {t(`firstFormSteps.maintenanceCalories.label`)}
                </span>
                <input
                  type="text"
                  className="border p-2 pl-3 rounded-md w-full bg-gray-100 cursor-not-allowed h-10 text-[14px]"
                  value={maintenanceCalories}
                  readOnly
                />
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-3 md:gap-2">
              <div className="w-full md:w-1/2">
                <span className="text-[#414651] text-[12px] sm:text-[14px] md:text-[16px] truncate max-w-[150px] block">
                  {t(`firstFormSteps.targetDailyCalories.label`)}
                </span>
                <input
                  type="text"
                  className="border p-2 pl-3 rounded-md w-full bg-gray-100 cursor-not-allowed h-10 text-[14px]"
                  value={targetDailyCalories}
                  readOnly
                />
              </div>

              <div className="w-full md:w-1/2">
                <span className="text-[#414651] text-[12px] sm:text-[14px] md:text-[16px] truncate max-w-[200px] block">
                  {t(`preferences.fields.dailyCalorieAdjustment`)}
                </span>
                <input
                  type="text"
                  className="border p-2 pl-3 rounded-md w-full bg-gray-100 cursor-not-allowed h-10 text-[14px]"
                  value={dailyCalorieAdjustment}
                  readOnly
                />
              </div>
            </div>
            

          </div>

          {/* Suggestion cards section (lg:col-span-2) */}
          <div className="lg:col-span-2">
            {message && (
              <div className="py-4 px-2 md:px-7 rounded-lg bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] h-full">
                <div className="flex justify-center mb-4">
                  <p className="bg-[#64DDFF] text-white rounded-full text-[16px] font-[Lato] font-[600] py-1 px-4 whitespace-nowrap overflow-hidden text-ellipsis">
                    {t("Our Professional Advice For You")}
                  </p>
                </div>
                <div
                  id="scrollable-quiz-container"
                  className="max-h-[320px] overflow-y-auto pr-2 custom-scrollbar space-y-2"
                >
                  {nutrientData.map(
                    ({ label, icon, value, tabColor }, index) => (
                      <div
                        key={index}
                        className="py-4 px-4 md:py-5 md:px-6 rounded-[16px] bg-white border border-[#E6E6E6] flex flex-col gap-2"
                        style={{
                          boxShadow: "7px 7px 9.5px 0px #0000000D",
                        }}
                      >
                        <div className="relative">
                          <div
                            className={`absolute border border-[#0575e6] -left-3 -top-1 rounded-full min-w-12 min-h-12 flex items-center justify-center border`}
                            style={{
                              backgroundColor: tabColor,
                            }}
                          >
                            {icon}
                          </div>
                          <p className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] whitespace-nowrap overflow-hidden text-ellipsis text-[14px] lg:text-[16px] font-[600] text-[#fff] py-2 px-8 md:px-10 rounded-full text-center lg:text-start min-w-[180px] md:min-w-[200px] max-w-full">
                            {label}
                          </p>
                        </div>
                        <div className="mt-2 md:mt-0">
                          <p className="-[14px] lg:text-[16px] font-[500] text-[#6C6A6A] font-[Lato] leading-relaxed">
                            {value}
                          </p>
                        </div>
                      </div>
                    )
                  )}
                </div>

                <style jsx>{`
                  .custom-scrollbar::-webkit-scrollbar {
                    width: 12px;
                  }

                  .custom-scrollbar::-webkit-scrollbar-track {
                    background-color: #fff !important;
                    border-radius: 10px;
                    margin: 4px 0;
                  }

                  .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: linear-gradient(
                      150deg,
                      #0771ef 12.89%,
                      #15aafc 85.22%
                    );
                    border-radius: 10px;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.3);
                  }
                `}</style>
              </div>
            )}
          </div>
        </div>


            {/* 💡 START OF IMPROVED DESIGN SECTION (INCLUDING NEW CARDS) 💡 */}
            <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-white shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-5 border-b pb-2">
                {t("Suggested Exercise Preferences")}
              </h3>

              {/* Responsive Grid: 2 columns on small/medium, 4 columns on large */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                
                {/* 1. Workout Categories Card (Occupies 2 columns on mobile/tablet) */}
                <div className="col-span-2 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <SportsGymnasticsIcon className="text-blue-500 mr-2" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      {t("Workout Categories")}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {suggestedWorkouts.length > 0 ? (
                      suggestedWorkouts.map((workout, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-200 text-blue-900 rounded-full text-xs font-medium"
                        >
                          {getDisplayName("workoutsCategories", workout)}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500">N/A</span>
                    )}
                  </div>
                </div>

                {/* 2. Equipment Category Card */}
                <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <FitnessCenterIcon className="text-green-500 mr-2" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      {t("Equipment")}
                    </span>
                  </div>
                  <span className="px-3 py-1 bg-green-200 text-green-900 rounded-full text-sm font-medium whitespace-nowrap">
                    {getDisplayName("equipmentCategories", suggestedEquipment) ||
                      "N/A"}
                  </span>
                </div>

                {/* 3. Difficulty Level Card */}
                <div className="p-4 bg-purple-50 border-l-4 border-purple-500 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <TuneIcon className="text-purple-500 mr-2" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      {t("Difficulty")}
                    </span>
                  </div>
                  <span className="px-3 py-1 bg-purple-200 text-purple-900 rounded-full text-sm font-medium capitalize whitespace-nowrap">
                    {suggestedDifficulty || "N/A"}
                  </span>
                </div>

                {/* 4. Daily Time Preference Card */}
                <div className="col-span-1 p-4 bg-orange-50 border-l-4 border-orange-500 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <AccessTimeIcon className="text-orange-500 mr-2" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      {t("Daily Time")}
                    </span>
                  </div>
                  <span className="px-3 py-1 bg-orange-200 text-orange-900 rounded-full text-sm font-medium whitespace-nowrap">
                    {dailyExerciseTimePreference || "N/A"}
                  </span>
                </div>

                {/* 5. Selectable Days Card (Occupies 3 columns on desktop/tablet) */}
                <div className="col-span-full md:col-span-3 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm">
                  <div className="flex items-center mb-2">
                    <CalendarMonthIcon className="text-red-500 mr-2" />
                    <span className="text-xs font-semibold text-gray-700 uppercase">
                      {t("Training Frequency")}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {selectableDays && selectableDays.length > 0 ? (
                      selectableDays.map((day, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-red-200 text-red-900 rounded-full text-xs font-medium"
                        >
                          {t(day)} {/* Assuming 't' can translate day names if needed */}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500">N/A</span>
                    )}
                  </div>
                </div>
                
              </div>
            </div>
            {/* 💡 END OF IMPROVED DESIGN SECTION 💡 */}
        {/* Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-[15px] mt-4 md:mt-3">
          <button
            onClick={() => setShowModal(false)}
            className={`px-4 py-3 md:py-[10px] bg-gray-300 text-gray-800 rounded-md w-full text-[14px] md:text-[16px]`}
          >
            {t(`firstFormSteps.confirmAdvisebuttonCancelText`)}
          </button>
          <button
            onClick={handleApplyGoal}
            className={`px-4 py-3 md:py-[10px] bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white rounded-md w-full text-[14px] md:text-[16px]`}
          >
            {t(`firstFormSteps.apply`)}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdviseModal;